import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * Full-page skeleton with gradient background, header, metric cards and content rows.
 * Use for pages like Analytics, UserManagement, Tasks, Communications.
 *
 * @param {number} metricCards - Number of metric/stat cards to show (default 4)
 * @param {number} rows - Number of content rows to show (default 5)
 * @param {boolean} showTabs - Whether to show a tab bar skeleton
 * @param {boolean} showToolbar - Whether to show a toolbar skeleton (search + filters)
 * @param {string} className - Additional wrapper classes
 */
export const PageSkeleton = ({
  metricCards = 4,
  rows = 5,
  showTabs = false,
  showToolbar = false,
  className,
}) => (
  <div className={cn('min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800', className)}>
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Toolbar */}
      {showToolbar && (
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 flex-1 max-w-sm" />
          <Skeleton className="h-10 w-28" />
          <Skeleton className="h-10 w-28" />
          <Skeleton className="h-10 w-28" />
        </div>
      )}

      {/* Metric cards */}
      {metricCards > 0 && (
        <div className={cn(
          'grid gap-4',
          metricCards <= 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
        )}>
          {Array.from({ length: metricCards }, (_, i) => (
            <Card key={i} className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-7 w-16" />
                </div>
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
              <Skeleton className="h-3 w-20 mt-2" />
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      {showTabs && (
        <div className="flex items-center gap-1 border-b pb-1">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-9 w-24" />
          ))}
        </div>
      )}

      {/* Content rows */}
      <Card className="p-4 space-y-3">
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center gap-4 p-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </Card>
    </div>
  </div>
);

/**
 * Skeleton for table-heavy pages (UserManagement, SuperAdmin tables).
 * Shows header, optional stat cards, and table rows.
 *
 * @param {number} columns - Number of table columns (default 5)
 * @param {number} rows - Number of table rows (default 6)
 * @param {number} statCards - Number of stat summary cards (default 0)
 * @param {string} className - Additional wrapper classes
 */
export const TablePageSkeleton = ({
  columns = 5,
  rows = 6,
  statCards = 0,
  className,
}) => (
  <div className={cn('space-y-6', className)}>
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <Skeleton className="h-7 w-44" />
        <Skeleton className="h-4 w-56" />
      </div>
      <Skeleton className="h-9 w-28" />
    </div>

    {/* Stat cards */}
    {statCards > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: statCards }, (_, i) => (
          <Card key={i} className="p-4">
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-7 w-12" />
          </Card>
        ))}
      </div>
    )}

    {/* Table */}
    <Card>
      <div className="p-4">
        {/* Table header */}
        <div className="flex items-center gap-4 border-b pb-3 mb-3">
          {Array.from({ length: columns }, (_, i) => (
            <Skeleton key={i} className="h-4 flex-1" />
          ))}
        </div>
        {/* Table rows */}
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center gap-4 py-3 border-b last:border-0">
            {Array.from({ length: columns }, (_, j) => (
              <Skeleton key={j} className={cn('h-4 flex-1', j === 0 && 'max-w-[200px]')} />
            ))}
          </div>
        ))}
      </div>
    </Card>
  </div>
);

/**
 * Skeleton for dashboard/chart pages (Analytics, CRM Management).
 * Shows header, metric cards, and chart placeholders.
 *
 * @param {number} metricCards - Number of metric cards (default 4)
 * @param {number} charts - Number of chart placeholders (default 2)
 * @param {string} className - Additional wrapper classes
 */
export const DashboardSkeleton = ({
  metricCards = 4,
  charts = 2,
  className,
}) => (
  <div className={cn('space-y-6', className)}>
    {/* Toolbar */}
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-48" />
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-36" />
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-9" />
      </div>
    </div>

    {/* Summary cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: metricCards }, (_, i) => (
        <Card key={i} className="p-5">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-3 w-16" />
          </div>
        </Card>
      ))}
    </div>

    {/* Chart areas */}
    <div className={cn('grid gap-6', charts > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1')}>
      {Array.from({ length: charts }, (_, i) => (
        <Card key={i} className="p-6">
          <Skeleton className="h-5 w-32 mb-4" />
          <Skeleton className="h-64 w-full" />
        </Card>
      ))}
    </div>
  </div>
);

/**
 * Skeleton for form/settings pages (BusinessProfile, APIIntegration).
 * Shows a card with form field placeholders.
 *
 * @param {number} sections - Number of form sections (default 3)
 * @param {number} fieldsPerSection - Number of fields per section (default 3)
 * @param {string} className - Additional wrapper classes
 */
export const FormSkeleton = ({
  sections = 3,
  fieldsPerSection = 3,
  className,
}) => (
  <div className={cn('space-y-6', className)}>
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-9 w-20" />
    </div>

    {/* Form sections */}
    {Array.from({ length: sections }, (_, i) => (
      <Card key={i}>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-3 w-56" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: fieldsPerSection }, (_, j) => (
            <div key={j} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * Skeleton for communication/inbox pages.
 * Shows a toolbar, filter tabs, and message list items.
 *
 * @param {number} items - Number of thread/message items (default 8)
 * @param {string} className - Additional wrapper classes
 */
export const InboxSkeleton = ({
  items = 8,
  className,
}) => (
  <div className={cn('min-h-screen bg-background', className)}>
    {/* Toolbar */}
    <div className="bg-card border-b px-4 py-3">
      <div className="flex items-center gap-3">
        <Skeleton className="h-9 w-48" />
        <div className="flex-1" />
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-9" />
      </div>
    </div>
    {/* Filter tabs */}
    <div className="bg-card border-b px-4 py-2">
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-8 w-20" />
        ))}
      </div>
    </div>
    {/* Thread list */}
    <div className="divide-y">
      {Array.from({ length: items }, (_, i) => (
        <div key={i} className="flex items-start gap-3 p-4">
          <Skeleton className="h-10 w-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Compact section-level skeleton for inline/embedded content areas.
 * Use inside cards, tabs, or dialogs instead of a spinner.
 *
 * @param {number} rows - Number of rows (default 4)
 * @param {string} className - Additional wrapper classes
 */
export const SectionSkeleton = ({
  rows = 4,
  className,
}) => (
  <div className={cn('space-y-3 py-4', className)}>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
        </div>
        <Skeleton className="h-6 w-14" />
      </div>
    ))}
  </div>
);

/**
 * Centered card skeleton for auth/invite/payment verification pages.
 *
 * @param {string} className - Additional wrapper classes
 */
export const CenteredCardSkeleton = ({
  className,
}) => (
  <div className={cn(
    'min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4',
    className
  )}>
    <Card className="w-full max-w-md">
      <CardContent className="pt-8 pb-8 space-y-6">
        <div className="flex justify-center">
          <Skeleton className="h-12 w-12 rounded-full" />
        </div>
        <div className="space-y-2 text-center">
          <Skeleton className="h-6 w-48 mx-auto" />
          <Skeleton className="h-4 w-64 mx-auto" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
        <Skeleton className="h-10 w-full" />
      </CardContent>
    </Card>
  </div>
);

/**
 * Minimal full-page skeleton used during auth checks (RouteGuards).
 * Shows a subtle centered pulsing logo placeholder.
 *
 * @param {string} className - Additional wrapper classes
 */
export const AuthCheckSkeleton = ({
  className,
}) => (
  <div className={cn(
    'min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center',
    className
  )}>
    <div className="text-center space-y-4">
      <Skeleton className="h-12 w-12 rounded-full mx-auto" />
      <Skeleton className="h-4 w-32 mx-auto" />
    </div>
  </div>
);
